import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { FaSpinner } from 'react-icons/fa';

// Atualizamos a interface para aceitar Promises no onClick
interface ButtonOption {
    icon?: string | React.ReactNode;
    title: string;
    description?: string;
    onClick: () => void | Promise<void>; // Aceita funções async
    disabled?: boolean;
}

interface ButtonListOptions {
    title?: string;
    message?: string;
    buttons: ButtonOption[];
    closeOnClick?: boolean; // Se true, fecha automaticamente após o sucesso da ação
}

// Componente separado com Estado para gerenciar o Loading
const ButtonListContent: React.FC<{
    title?: string;
    message?: string;
    buttons: ButtonOption[];
    closeOnClick: boolean;
    onClose: () => void;
}> = ({ title, message, buttons, closeOnClick, onClose }) => {
    
    // Estado para saber qual botão está carregando (pelo índice)
    const [loadingIndex, setLoadingIndex] = useState<number | null>(null);
    // Botões já executados com sucesso (feedback visual)
    const [doneIndices, setDoneIndices] = useState<Set<number>>(new Set());

    const handleButtonClick = async (button: ButtonOption, index: number) => {
        if (button.disabled || loadingIndex !== null) return;

        setLoadingIndex(index);

        try {
            await button.onClick();

            if (closeOnClick) {
                onClose();
            } else {
                // Marca como concluído para feedback visual
                setDoneIndices(prev => new Set(prev).add(index));
            }
        } catch (error) {
            console.error("Erro ao executar ação do botão:", error);
        } finally {
            setLoadingIndex(null);
        }
    };

    const getButtonStyle = (disabled?: boolean, isLoading?: boolean): React.CSSProperties => {
        return {
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            width: '100%',
            padding: '14px 18px',
            border: '1px solid #e0e0e0',
            borderRadius: '10px',
            cursor: (disabled || isLoading) ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            textAlign: 'left' as const,
            fontFamily: 'Segoe UI, Roboto, sans-serif',
            fontSize: '15px',
            // Se estiver desabilitado ou se OUTRO botão estiver carregando, diminui a opacidade
            opacity: disabled ? 0.6 : (loadingIndex !== null && !isLoading) ? 0.5 : 1,
            backgroundColor: 'white',
            color: '#333',
            position: 'relative'
        };
    };

    const useGrid = buttons.length >= 4;

    return (
        <div
            style={{
                padding: '28px 24px',
                color: '#333',
                minWidth: useGrid ? '520px' : '320px',
                maxWidth: useGrid ? '600px' : '420px',
                fontFamily: 'Segoe UI, Roboto, sans-serif',
            }}
        >
            {/* Estilo para animação de rotação do spinner */}
            <style>
                {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}
            </style>

            {/* Cabeçalho */}
            {(title || message) && (
                <div style={{ marginBottom: '20px' }}>
                    {title && (
                        <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#1F467E' }}>
                            {title}
                        </h3>
                    )}
                    {message && (
                        <p style={{ margin: '6px 0 0 0', fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
                            {message}
                        </p>
                    )}
                </div>
            )}

            {/* Lista de Botões — grid 2 colunas quando há 4+ botões */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: useGrid ? 'repeat(2, 1fr)' : '1fr',
                gap: '10px',
            }}>
                {buttons.map((button, index) => {
                    const isLoading = loadingIndex === index;
                    const isAnyLoading = loadingIndex !== null;
                    const isDone = doneIndices.has(index);

                    return (
                        <button
                            key={index}
                            onClick={() => handleButtonClick(button, index)}
                            disabled={button.disabled || isAnyLoading}
                            style={{
                                ...getButtonStyle(button.disabled, isLoading),
                                flexDirection: useGrid ? 'column' : 'row',
                                textAlign: useGrid ? 'center' : 'left',
                                padding: useGrid ? '16px 12px' : '14px 18px',
                                minHeight: useGrid ? '90px' : 'auto',
                                justifyContent: useGrid ? 'center' : 'flex-start',
                                borderColor: isDone ? '#4CAF50' : undefined,
                                backgroundColor: isDone ? 'rgba(76, 175, 80, 0.04)' : undefined,
                            } as React.CSSProperties}
                            onMouseOver={(e) => {
                                if (!button.disabled && !isAnyLoading) {
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.12)';
                                    if (!isDone) {
                                        e.currentTarget.style.backgroundColor = 'rgba(31, 70, 126, 0.05)';
                                        e.currentTarget.style.borderColor = '#1F467E';
                                    }
                                }
                            }}
                            onMouseOut={(e) => {
                                if (!button.disabled && !isAnyLoading) {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                    e.currentTarget.style.backgroundColor = isDone ? 'rgba(76, 175, 80, 0.04)' : 'white';
                                    e.currentTarget.style.borderColor = isDone ? '#4CAF50' : '#e0e0e0';
                                }
                            }}
                        >
                            {/* Ícone ou Spinner */}
                            <div
                                style={{
                                    width: useGrid ? '28px' : '22px',
                                    height: useGrid ? '28px' : '22px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: isDone ? '#4CAF50' : '#1F467E',
                                    fontSize: useGrid ? '22px' : '18px',
                                    margin: useGrid ? '0 auto 6px' : '0',
                                }}
                            >
                                {isLoading ? (
                                    <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                                ) : (
                                    button.icon
                                )}
                            </div>

                            <div style={{ flex: useGrid ? undefined : 1 }}>
                                <div style={{
                                    fontSize: useGrid ? '13px' : '15px',
                                    fontWeight: 600,
                                    marginBottom: button.description && !useGrid ? '2px' : '0',
                                    color: isDone ? '#4CAF50' : '#333',
                                }}>
                                    {isDone ? `✓ ${button.title}` : button.title}
                                </div>
                                {button.description && !useGrid && (
                                    <div style={{
                                        fontSize: '13px',
                                        color: '#666',
                                        lineHeight: '1.3',
                                    }}>
                                        {isLoading ? "Processando..." : button.description}
                                    </div>
                                )}
                            </div>
                        </button>
                    )
                })}
            </div>

            {/* Botão Fechar */}
            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'right' }}>
                <button
                    onClick={onClose}
                    disabled={loadingIndex !== null}
                    style={{
                        background: '#f8f9fa',
                        color: loadingIndex !== null ? '#aaa' : '#444',
                        border: '1px solid #ddd',
                        padding: '8px 26px',
                        borderRadius: '8px',
                        cursor: loadingIndex !== null ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: 500,
                        transition: 'all 0.2s',
                    }}
                    onMouseOver={(e) => {
                        if (loadingIndex === null) e.currentTarget.style.backgroundColor = '#ececec';
                    }}
                    onMouseOut={(e) => {
                        if (loadingIndex === null) e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }}
                >
                    Concluir
                </button>
            </div>
        </div>
    );
};

export const showButtonList = (options: ButtonListOptions) => {
    const { title, message, buttons, closeOnClick = true } = options;

    return new Promise<void>((resolve) => {
        const container = document.createElement('div');
        document.body.appendChild(container);

        const handleClose = () => {
            ReactDOM.unmountComponentAtNode(container);
            if (container.parentNode) container.parentNode.removeChild(container);
            resolve();
        };

        ReactDOM.render(
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: 'rgba(0, 0, 0, 0.55)',
                    zIndex: 9998,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
                onClick={handleClose}
            >
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        background: 'rgba(255, 255, 255, 0.98)',
                        borderRadius: '14px',
                        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.25)',
                        zIndex: 9999,
                        maxHeight: '90vh',
                        overflowY: 'auto',
                    }}
                >
                    <ButtonListContent
                        title={title}
                        message={message}
                        buttons={buttons}
                        closeOnClick={closeOnClick}
                        onClose={handleClose}
                    />
                </div>
            </div>,
            container
        );
    });
};
